export const simpleFinanceCourse = {
  id: "finance-mastery-complete-2024",
  title: "Finance Mastery: Complete Investment & Analysis Course",
  shortDescription: "Complete 24+ hour comprehensive finance course covering everything from fundamentals to advanced portfolio management, derivatives, and corporate finance.",
  description: "Master finance from fundamentals to advanced corporate finance, derivatives, and portfolio management with 24+ hours of comprehensive learning including real-time market analysis, financial modeling, risk management, and practical case studies from real companies.",
  category: "Finance",
  industryCategory: "Financial Services",
  difficulty: "intermediate",
  duration: 24,
  estimatedWeeks: 12,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from certified financial professionals and market analysts",
  thumbnailUrl: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),
  
  learningObjectives: [
    "Analyze financial statements and key performance metrics",
    "Build dynamic financial models and projections",
    "Evaluate stocks using real-time market data",
    "Create balanced investment portfolios",
    "Calculate risk-adjusted returns and portfolio optimization",
    "Master DCF valuation and comparative analysis",
    "Apply modern portfolio theory principles",
    "Develop trading and investment strategies",
    "Understand derivatives and options trading",
    "Master corporate finance and capital structure decisions"
  ],

  prerequisites: [
    "Basic Mathematics",
    "Excel/Google Sheets", 
    "Economic Fundamentals"
  ],

  skills: [
    "Financial Analysis",
    "Stock Valuation",
    "Portfolio Management",
    "Risk Assessment", 
    "Financial Modeling",
    "Market Analysis",
    "Investment Strategy",
    "Balance Sheet Analysis",
    "Options Trading",
    "Corporate Finance"
  ],

  tags: [
    "Finance",
    "Investing",
    "Stocks", 
    "Portfolio",
    "Analysis",
    "Markets",
    "Valuation",
    "Risk Management",
    "Derivatives",
    "Corporate Finance"
  ],

  marketData: {
    averageSalary: 95000,
    salaryRange: {
      min: 65000,
      max: 145000
    },
    jobDemand: 4,
    marketTrend: "growing",
    careerPaths: [
      "Financial Analyst",
      "Portfolio Manager",
      "Investment Advisor", 
      "Risk Manager",
      "Investment Banking Analyst",
      "Corporate Finance Manager"
    ],
    relatedSkills: [
      "Excel",
      "Python",
      "Bloomberg Terminal",
      "Financial Modeling",
      "CFA",
      "Statistics",
      "SQL",
      "R Programming"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  // Simplified module structure for Firestore compatibility
  moduleCount: 9,
  moduleIds: [
    "finance-fundamentals",
    "stock-analysis", 
    "portfolio-management",
    "financial-modeling",
    "derivatives-risk-management",
    "corporate-finance",
    "fixed-income-securities",
    "behavioral-finance",
    "advanced-topics"
  ],

  // Simplified module structure with required content array
  modules: [
    {
      id: "finance-fundamentals",
      title: "Financial Fundamentals",
      description: "Master core financial concepts, statements, and analysis techniques",
      order: 1,
      estimatedHours: 3.5,
      content: [
        {
          id: "intro-to-finance",
          title: "Introduction to Finance",
          type: "video",
          duration: 20
        },
        {
          id: "financial-statements",
          title: "Reading Financial Statements",
          type: "text",
          duration: 25
        },
        {
          id: "balance-sheet-interactive",
          title: "Balance Sheet Exercise",
          type: "interactive",
          duration: 30
        }
      ],
      quiz: {
        id: "fundamentals-quiz",
        title: "Financial Fundamentals Quiz",
        timeLimit: 15,
        passingScore: 75,
        attempts: 3
      }
    },
    {
      id: "stock-analysis",
      title: "Stock Analysis & Valuation",
      description: "Learn to analyze and value stocks using real-time market data", 
      order: 2,
      estimatedHours: 3.5,
      content: [
        {
          id: "stock-basics",
          title: "Stock Market Fundamentals",
          type: "video",
          duration: 25
        },
        {
          id: "valuation-methods",
          title: "Stock Valuation Methods",
          type: "video",
          duration: 35
        },
        {
          id: "real-time-analysis",
          title: "Real-Time Stock Analysis",
          type: "interactive",
          duration: 40
        }
      ],
      quiz: {
        id: "stock-analysis-quiz",
        title: "Stock Analysis Quiz",
        timeLimit: 12,
        passingScore: 80,
        attempts: 3
      }
    },
    {
      id: "portfolio-management", 
      title: "Portfolio Management",
      description: "Build and optimize investment portfolios using modern portfolio theory",
      order: 3,
      estimatedHours: 3,
      content: [
        {
          id: "portfolio-theory",
          title: "Modern Portfolio Theory",
          type: "video",
          duration: 30
        },
        {
          id: "asset-allocation",
          title: "Asset Allocation Strategies",
          type: "text",
          duration: 25
        },
        {
          id: "portfolio-optimizer",
          title: "Portfolio Optimization Tool",
          type: "interactive",
          duration: 35
        }
      ],
      quiz: {
        id: "portfolio-quiz",
        title: "Portfolio Management Quiz",
        timeLimit: 10,
        passingScore: 75,
        attempts: 3
      }
    },
    {
      id: "financial-modeling",
      title: "Financial Modeling", 
      description: "Build comprehensive financial models and perform DCF analysis",
      order: 4,
      estimatedHours: 3.5,
      content: [
        {
          id: "dcf-modeling",
          title: "DCF Valuation Model",
          type: "video",
          duration: 40
        },
        {
          id: "dcf-interactive",
          title: "Interactive DCF Builder",
          type: "interactive",
          duration: 45
        }
      ],
      quiz: {
        id: "financial-modeling-quiz",
        title: "Financial Modeling Quiz",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3
      }
    },
    {
      id: "derivatives-risk-management",
      title: "Derivatives & Risk Management",
      description: "Master options, futures, and advanced risk management techniques",
      order: 5,
      estimatedHours: 3,
      content: [
        {
          id: "options-fundamentals",
          title: "Options Trading Fundamentals",
          type: "video",
          duration: 35
        },
        {
          id: "options-strategies",
          title: "Advanced Options Strategies",
          type: "text",
          duration: 40
        },
        {
          id: "risk-calculator",
          title: "Options Risk Calculator",
          type: "interactive",
          duration: 35
        }
      ],
      quiz: {
        id: "derivatives-quiz",
        title: "Derivatives & Risk Management Quiz",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3
      }
    },
    {
      id: "corporate-finance",
      title: "Corporate Finance", 
      description: "Capital structure, M&A, and corporate financial decision making",
      order: 6,
      estimatedHours: 3.5,
      content: [
        {
          id: "capital-structure",
          title: "Capital Structure Optimization",
          type: "text",
          duration: 45
        },
        {
          id: "ma-analysis",
          title: "Mergers & Acquisitions Analysis",
          type: "video",
          duration: 50
        },
        {
          id: "lbo-modeling",
          title: "Leveraged Buyout Modeling",
          type: "interactive",
          duration: 45
        }
      ],
      quiz: {
        id: "corporate-finance-quiz",
        title: "Corporate Finance Quiz",
        timeLimit: 12,
        passingScore: 80,
        attempts: 3
      }
    },
    {
      id: "fixed-income-securities",
      title: "Fixed Income Securities",
      description: "Bond analysis, yield curves, and fixed income portfolio management",
      order: 7, 
      estimatedHours: 2.5,
      content: [
        {
          id: "bond-fundamentals",
          title: "Bond Valuation and Analysis",
          type: "text",
          duration: 35
        },
        {
          id: "yield-curve-modeling",
          title: "Yield Curve Analysis Tool",
          type: "interactive",
          duration: 30
        }
      ],
      quiz: {
        id: "fixed-income-quiz",
        title: "Fixed Income Quiz",
        timeLimit: 10,
        passingScore: 75,
        attempts: 3
      }
    },
    {
      id: "behavioral-finance",
      title: "Behavioral Finance",
      description: "Psychology of financial markets and investor behavior", 
      order: 8,
      estimatedHours: 2,
      content: [
        {
          id: "cognitive-biases",
          title: "Cognitive Biases in Finance",
          type: "text",
          duration: 30
        },
        {
          id: "market-psychology",
          title: "Market Cycles and Psychology",
          type: "video",
          duration: 25
        },
        {
          id: "bias-assessment",
          title: "Investment Bias Assessment",
          type: "interactive",
          duration: 25
        }
      ],
      quiz: {
        id: "behavioral-finance-quiz",
        title: "Behavioral Finance Quiz",
        timeLimit: 10,
        passingScore: 75,
        attempts: 3
      }
    },
    {
      id: "advanced-topics", 
      title: "Advanced Finance Topics",
      description: "Quantitative finance, alternative investments, and emerging trends",
      order: 9,
      estimatedHours: 2.5,
      content: [
        {
          id: "quantitative-methods",
          title: "Quantitative Finance Methods",
          type: "text",
          duration: 35
        },
        {
          id: "alternative-investments",
          title: "Alternative Investments Overview",
          type: "video",
          duration: 30
        },
        {
          id: "portfolio-simulation",
          title: "Advanced Portfolio Simulation",
          type: "interactive",
          duration: 25
        }
      ],
      quiz: {
        id: "advanced-topics-quiz",
        title: "Advanced Topics Quiz",
        timeLimit: 12,
        passingScore: 80,
        attempts: 3
      }
    }
  ],

  // Simplified assignment structure
  assignmentCount: 3,
  assignmentIds: [
    "financial-analysis-project",
    "portfolio-construction", 
    "options-trading-strategy"
  ],

  assignments: [
    {
      id: "financial-analysis-project",
      title: "Company Financial Analysis Project",
      description: "Complete financial analysis of a public company including ratio analysis, valuation, and investment recommendation",
      type: "project",
      dueWeeks: 4,
      maxScore: 100
    },
    {
      id: "portfolio-construction", 
      title: "Portfolio Construction and Optimization",
      description: "Build and optimize a diversified investment portfolio using modern portfolio theory",
      type: "project",
      dueWeeks: 3,
      maxScore: 100
    },
    {
      id: "options-trading-strategy",
      title: "Options Trading Strategy Analysis", 
      description: "Design and analyze a comprehensive options trading strategy",
      type: "case-study",
      dueWeeks: 2,
      maxScore: 75
    }
  ],

  // Simplified final exam
  finalExam: {
    id: "finance-mastery-final",
    title: "Finance Mastery Final Exam", 
    description: "Comprehensive exam covering all finance concepts and analysis techniques",
    timeLimit: 90,
    passingScore: 85,
    attempts: 2,
    questionCount: 20
  },

  // Remove complex nested structures that cause Firestore issues
  quizzes: []
};